import { Autocomplete } from '@base-ui/react';
import * as React from 'react';

import { DimeComboboxContent } from './DimeCombobox.js';
import { DimeInput } from './DimeInput.js';

type DimeSuggestInputProps = Readonly<
  Pick<
    React.ComponentProps<'input'>,
    | 'id'
    | 'name'
    | 'placeholder'
    | 'maxLength'
    | 'disabled'
    | 'onBlur'
    | 'aria-invalid'
    | 'aria-describedby'
    | 'aria-label'
    | 'aria-labelledby'
  > & {
    value: string;
    /** Receives every keystroke and every picked suggestion. */
    onValueChange: (value: string) => void;
    suggestions: readonly string[];
  }
>;

/** Free text with suggestions: any typed value stands, and the list only narrows and fills. */
export function DimeSuggestInput({
  value,
  onValueChange,
  suggestions,
  onBlur,
  ...inputProps
}: DimeSuggestInputProps) {
  const [open, setOpen] = React.useState(false);
  const { contains } = Autocomplete.useFilter();
  const hasMatch = suggestions.some((suggestion) => contains(suggestion, value.trim()));

  return (
    <Autocomplete.Root
      items={suggestions}
      value={value}
      onValueChange={onValueChange}
      // An open popup with no matches still marks the rest of the dialog aria-hidden, so it stays closed.
      open={open && hasMatch}
      onOpenChange={setOpen}
      openOnInputClick
    >
      <Autocomplete.Input
        render={
          <DimeInput
            {...inputProps}
            onBlur={(event) => {
              setOpen(false);
              onBlur?.(event);
            }}
          />
        }
      />
      <DimeComboboxContent className="data-empty:hidden">
        <Autocomplete.List className="max-h-60 overflow-y-auto overscroll-contain p-1 data-empty:p-0">
          {(suggestion: string) => (
            <Autocomplete.Item
              key={suggestion}
              value={suggestion}
              className="flex w-full cursor-default items-center rounded-md px-1.5 py-1 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
            >
              {suggestion}
            </Autocomplete.Item>
          )}
        </Autocomplete.List>
      </DimeComboboxContent>
    </Autocomplete.Root>
  );
}
